import React, { useState } from 'react';
import {
  MI_AD_BLUEPRINT,
  MI_ATTACK_CLUSTERS,
  MI_BUDGET_SPLIT,
  MI_COMMENTARY,
  MI_CPC_DAYS,
  MI_FOCUS,
  MI_KW_CLUSTERS,
  MI_LANDING_BLUEPRINT,
  MI_OFFERS,
  MI_OVERVIEW_METRICS,
  MI_SEGMENT_STATS,
  MI_SEGMENTS,
  MI_SEO_KEYWORDS,
  MI_SEO_METRICS,
  MI_SEO_STRATEGY,
  MI_SIGNALS,
  MI_SWOT,
} from '../../growth-market/mockData';
import type { MiMarketData } from '../../growth-market/types';
import {
  deltaToneClass,
  MiAiBox,
  MiAiList,
  MiAutopilot,
  MiBadge,
  MiBar,
  MiCard,
  miEyebrowClass,
  MI_TONE,
  MiSectionHead,
} from './MarketIntelligenceParts';

function MiEmptyNote({ children = 'No data yet' }: { children?: string }) {
  return <div className="py-3 text-center text-xs italic text-[var(--ink-4)]">{children}</div>;
}

export function MiTabOverview({ data }: { data?: MiMarketData }) {
  const connected = data !== undefined;
  const metrics = connected ? data.overviewMetrics : MI_OVERVIEW_METRICS;
  const swot = connected ? data.swot : MI_SWOT;
  const commentary = connected ? data.commentary : MI_COMMENTARY;
  const signals = connected ? data.signals : MI_SIGNALS;
  const swotSubtitle = connected ? data.swotSubtitle : 'Aggregated from all 8 competitors';

  return (
    <>
      <div
        className="mi-metrics-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {metrics.map((metric) => (
          <MiCard key={metric.label} className="px-5 py-[18px]">
            <div className={miEyebrowClass}>{metric.label}</div>
            <div className="mt-2.5 font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em] text-[var(--ink-0)]">
              {metric.value}
            </div>
            {metric.delta ? (
              <div className={`mt-[7px] text-xs font-medium ${deltaToneClass(metric.tone)}`}>
                {metric.delta}
              </div>
            ) : null}
          </MiCard>
        ))}
      </div>

      <div
        className="mi-overview-grid mb-6 grid gap-4"
        style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <MiCard>
          <MiSectionHead icon="bars" tone="ai" title="Market SWOT" sub={swotSubtitle} />
          <div className="mi-swot-grid grid grid-cols-2 gap-2.5">
            {swot.map((swotItem) => {
              const tone = MI_TONE[swotItem.tone];
              return (
                <div
                  key={swotItem.title}
                  className="rounded-[var(--r-md)] p-[15px_16px]"
                  style={{ background: tone.tint }}
                >
                  <div
                    className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ color: tone.accent }}
                  >
                    {swotItem.title}
                  </div>
                  {swotItem.items.length ? (
                    <ul className="m-0 flex list-disc flex-col gap-1.5 pl-[15px]">
                      {swotItem.items.map((item) => (
                        <li key={item} className="text-xs leading-[1.5] text-[var(--ink-1)]">
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <MiEmptyNote />
                  )}
                </div>
              );
            })}
          </div>
        </MiCard>

        <MiAiBox title="ELVA Market Commentary">
          <MiAiList label="What's happening" items={commentary.happening} />
          <MiAiList label="How Autopilot is responding" items={commentary.responding} />
          <MiAiList label="Recommended human decisions" divider items={commentary.recommended} />
        </MiAiBox>
      </div>

      <MiCard className="mb-6">
        <MiSectionHead
          icon="broadcast"
          tone="ai"
          title="Aggregate market signals"
          right={<MiAutopilot />}
        />
        <div
          className="mi-signals-grid grid gap-[18px]"
          style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}
        >
          {signals.map((group) => (
            <div key={group.label}>
              <div className={`${miEyebrowClass} mb-3`}>{group.label}</div>
              {group.items.length ? (
                group.items.map((item) => (
                  <MiBar key={item.name} label={item.name} value={item.value} />
                ))
              ) : (
                <MiEmptyNote />
              )}
            </div>
          ))}
        </div>
      </MiCard>
    </>
  );
}

export function MiTabAds({ data }: { data?: MiMarketData }) {
  const connected = data !== undefined;
  const kwClusters = connected ? data.kwClusters : MI_KW_CLUSTERS;
  const cpcDays = connected ? data.cpcDays : MI_CPC_DAYS;
  const offers = connected ? data.offers : MI_OFFERS;
  const offerTotal = connected ? data.offerCompetitorTotal : 8;
  const focus = connected ? data.focusClusters : MI_FOCUS;
  const attackClusters = connected ? data.attackClusters : MI_ATTACK_CLUSTERS;
  const budgetSplit = connected ? data.budgetSplit : MI_BUDGET_SPLIT;
  const adBlueprint = connected ? data.adBlueprint : MI_AD_BLUEPRINT;
  const landingBlueprint = connected ? data.landingBlueprint : MI_LANDING_BLUEPRINT;
  const maxCpc = 8;

  return (
    <>
      <div
        className="mi-ads-grid mb-6 grid gap-4"
        style={{ gridTemplateColumns: '1fr 1fr 1fr' }}
      >
        <MiCard>
          <MiSectionHead icon="target" tone="ai" title="Paid keyword clusters" right={<MiAutopilot />} />
          {kwClusters.length ? (
            kwClusters.map((cluster) => (
              <MiBar key={cluster.keyword} label={cluster.keyword} value={cluster.share} tone={cluster.tone} />
            ))
          ) : (
            <MiEmptyNote />
          )}
        </MiCard>

        <MiCard>
          <MiSectionHead icon="pulse" tone="info" title="CPC pressure index" right={<MiAutopilot />} />
          {cpcDays.length ? (
            <div className="flex flex-col gap-[9px]">
              {cpcDays.map((day) => {
                const toneKey = day.value > 6 ? 'terra' : day.value > 4 ? 'gold' : 'success';
                return (
                  <div key={day.day} className="flex items-center gap-2.5">
                    <span className="w-7 text-[11px] font-medium text-[var(--ink-4)]">{day.day}</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded bg-[var(--surface-mute)]">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${(day.value / maxCpc) * 100}%`,
                          background: MI_TONE[toneKey].accent,
                        }}
                      />
                    </div>
                    <span className="w-[34px] text-right font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)]">
                      ${day.value}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <MiEmptyNote />
          )}
          <div className="mt-[13px] text-[11.5px] italic text-[var(--ink-4)]">
            Autopilot shifts budget to low-CPC days automatically
          </div>
        </MiCard>

        <MiCard>
          <MiSectionHead icon="bolt" tone="gold" title="Offer usage in ads" />
          <div className="flex flex-col gap-2">
            {offers.length ? (
              offers.map((offer) => (
                <div
                  key={offer.offer}
                  className="flex items-center justify-between gap-2.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-[13px] py-[9px]"
                >
                  <span className="text-[12.5px] text-[var(--ink-1)]">{offer.offer}</span>
                  <MiBadge tone="neutral">
                    {offer.count} of {offerTotal}
                  </MiBadge>
                </div>
              ))
            ) : (
              <MiEmptyNote />
            )}
          </div>
        </MiCard>
      </div>

      <MiCard className="mb-6">
        <MiSectionHead
          icon="target"
          tone="ai"
          title="Recommended focus clusters"
          right={<MiAutopilot />}
          sub="Keywords where ELVA sees the best ROI opportunity based on volume, CPC, and competitor gaps"
        />
        {focus.length ? (
          <div className="flex flex-wrap gap-2.5">
            {focus.map((cluster) => (
              <div
                key={cluster.label}
                className="min-w-[180px] flex-[1_1_180px] rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] p-[13px_16px]"
              >
                <MiBadge tone={cluster.tone}>{cluster.label}</MiBadge>
                <div className="mt-[7px] text-xs leading-[1.5] text-[var(--ink-3)]">{cluster.reason}</div>
              </div>
            ))}
          </div>
        ) : (
          <MiEmptyNote />
        )}
      </MiCard>

      <MiAiBox title="ELVA — Google Ads Strategy">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="mb-[11px] text-[12.5px] font-semibold text-white/90">Top attack clusters</div>
            {attackClusters.length ? (
              attackClusters.map((attack) => (
                <div key={attack.name} className="mb-[13px]">
                  <div className="mb-[5px] text-[12.5px] text-white/85">{attack.name}</div>
                  <div className="h-1.5 overflow-hidden rounded bg-white/15">
                    <div
                      className="h-full rounded bg-[var(--ai-g2,#C9B6FF)]"
                      style={{ width: `${attack.value}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <MiEmptyNote />
            )}
            <div className="mb-[11px] mt-5 text-[12.5px] font-semibold text-white/90">
              Budget split recommendation
            </div>
            {budgetSplit.length ? (
              <div className="flex overflow-hidden rounded-[var(--r-sm)]">
                {budgetSplit.map((segment) => (
                  <div
                    key={segment.label}
                    className="flex h-[30px] items-center justify-center text-[10.5px] font-bold text-white"
                    style={{
                      flex: segment.flex,
                      background:
                        segment.tone === 'gold'
                          ? 'var(--gold)'
                          : segment.tone === 'lav'
                            ? 'var(--ai-5)'
                            : 'var(--ai-4)',
                      color: segment.tone === 'gold' ? 'var(--ink-0)' : '#fff',
                    }}
                  >
                    {segment.label}
                  </div>
                ))}
              </div>
            ) : (
              <MiEmptyNote />
            )}
          </div>
          <div>
            <MiAiList label="Ad group structure blueprint" items={adBlueprint} />
            <div className="mb-2.5 text-[12.5px] font-semibold text-white/90">Landing page blueprint</div>
            {landingBlueprint.length ? (
              <div className="flex flex-col gap-[7px]">
                {landingBlueprint.map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: item.active ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.3)' }}
                    />
                    <span
                      className="text-[12.5px]"
                      style={{ color: item.active ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)' }}
                    >
                      {item.label}
                    </span>
                    {item.active ? (
                      <span className="rounded-[var(--r-pill)] bg-white/20 px-[7px] py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-white">
                        Active
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <MiEmptyNote />
            )}
          </div>
        </div>
      </MiAiBox>
    </>
  );
}

export function MiTabSeo({ data }: { data?: MiMarketData }) {
  const connected = data !== undefined;
  const seoMetrics = connected ? data.seoMetrics : MI_SEO_METRICS;
  const seoKeywords = connected ? data.seoKeywords : MI_SEO_KEYWORDS;
  const seoStrategy = connected ? data.seoStrategy : MI_SEO_STRATEGY;

  return (
    <>
      <div
        className="mi-metrics-grid mb-6 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {seoMetrics.map((metric) => (
          <MiCard key={metric.label} className="px-5 py-[18px]">
            <div className={miEyebrowClass}>{metric.label}</div>
            {metric.value ? (
              <div className="mt-2 font-[var(--font-display)] text-[22px] font-extrabold tracking-[-0.03em] text-[var(--ink-0)]">
                {metric.value}
                {metric.sub ? (
                  <span className="text-[13px] font-semibold text-[var(--ink-4)]"> {metric.sub}</span>
                ) : null}
              </div>
            ) : null}
            {metric.tags?.length ? (
              <div className="mt-2.5 flex flex-wrap gap-[5px]">
                {metric.tags.map((tag) => (
                  <MiBadge key={tag} tone={metric.tone ?? 'neutral'}>
                    {tag}
                  </MiBadge>
                ))}
              </div>
            ) : connected && !metric.value ? (
              <MiEmptyNote />
            ) : null}
          </MiCard>
        ))}
      </div>

      <div className="mi-seo-grid mb-6 grid gap-4" style={{ gridTemplateColumns: '1.2fr 0.8fr' }}>
        <MiCard>
          <MiSectionHead
            icon="search"
            tone="ai"
            title="Local keyword opportunities"
            right={<MiAutopilot />}
            sub="Keywords ELVA monitors for your SEO content strategy"
          />
          <div className="overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)]">
            <div
              className={`grid bg-[var(--surface-mute)] px-3.5 py-[9px] ${miEyebrowClass}`}
              style={{ gridTemplateColumns: '1.6fr 0.8fr 0.7fr 1fr' }}
            >
              <span>Keyword</span>
              <span>Intent</span>
              <span>Difficulty</span>
              <span>Page type</span>
            </div>
            {seoKeywords.length ? (
              seoKeywords.map((row) => (
                <div
                  key={row.keyword}
                  className="grid items-center border-t border-[var(--cream-2)] px-3.5 py-[11px]"
                  style={{ gridTemplateColumns: '1.6fr 0.8fr 0.7fr 1fr' }}
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--ink-0)]">{row.keyword}</div>
                    <div className="mt-px text-[11px] italic text-[var(--ink-4)]">{row.note}</div>
                  </div>
                  <span>
                    <MiBadge tone={row.intent === 'High' ? 'terra' : 'gold'}>{row.intent}</MiBadge>
                  </span>
                  <span className="font-[var(--font-mono)] text-xs text-[var(--ink-2)]">{row.difficulty}</span>
                  <span className="text-xs text-[var(--ink-3)]">{row.page}</span>
                </div>
              ))
            ) : (
              <MiEmptyNote />
            )}
          </div>
        </MiCard>

        <MiAiBox title="ELVA — Local SEO Strategy">
          <MiAiList label="Content to build" items={seoStrategy.content} />
          <MiAiList label="Pages to build" items={seoStrategy.pages} />
          <MiAiList label="Pages to watch" divider items={seoStrategy.watch} />
        </MiAiBox>
      </div>
    </>
  );
}

export function MiTabSegments({ data }: { data?: MiMarketData }) {
  const connected = data !== undefined;
  const segments = connected ? data.segments : MI_SEGMENTS;
  const segmentStats = connected ? data.segmentStats : MI_SEGMENT_STATS;
  const [segment, setSegment] = useState(0);

  return (
    <MiCard className="mb-6">
      <MiSectionHead
        icon="team"
        tone="ai"
        title="Patient Segment Explorer"
        right={<MiAutopilot />}
        sub="How ELVA segments the local market to target ads and content"
      />
      <div className="mb-[22px] inline-flex flex-wrap gap-[3px] rounded-[var(--r-pill)] bg-[var(--surface-mute)] p-1">
        {segments.length ? (
          segments.map((name, index) => {
            const active = segment === index;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setSegment(index)}
                className={[
                  'cursor-pointer rounded-[var(--r-pill)] border-0 px-[15px] py-1.5 font-[var(--font-body)] text-[12.5px]',
                  active
                    ? 'bg-[var(--cream-1)] font-semibold text-[var(--ink-0)] shadow-[var(--sh-1)]'
                    : 'bg-transparent font-medium text-[var(--ink-3)]',
                ].join(' ')}
              >
                {name}
              </button>
            );
          })
        ) : (
          <MiEmptyNote />
        )}
      </div>

      <div
        className="mi-segment-stats mb-[22px] grid gap-3.5"
        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
      >
        {segmentStats.map((stat) => (
          <div
            key={stat.label}
            className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] p-[18px]"
          >
            <div className={miEyebrowClass}>{stat.label}</div>
            <div className="mb-0.5 mt-2 font-[var(--font-display)] text-[22px] font-extrabold tracking-[-0.03em] text-[var(--ink-0)]">
              {stat.value}
            </div>
            <div className="text-xs text-[var(--ink-3)]">{stat.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="mb-2.5 text-[12.5px] font-semibold text-[var(--ink-1)]">
            Ads / SEO approach for this segment
          </div>
          {connected ? (
            <MiEmptyNote />
          ) : (
            <div className="flex flex-col gap-2">
              <div className="rounded-[var(--r-md)] border-l-[3px] border-[var(--ai-3)] bg-[var(--ai-tint)] px-3.5 py-[11px]">
                <div className="mb-[3px] text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ai-3)]">
                  Google Ads
                </div>
                <div className="text-[12.5px] leading-[1.5] text-[var(--ink-1)]">
                  Target “best family dentist” + neighborhood variants. Use review count in ad copy as social
                  proof.
                </div>
              </div>
              <div className="rounded-[var(--r-md)] border-l-[3px] border-[var(--info)] bg-[var(--info-tint)] px-3.5 py-[11px]">
                <div className="mb-[3px] text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--info)]">
                  SEO content
                </div>
                <div className="text-[12.5px] leading-[1.5] text-[var(--ink-1)]">
                  Create “First Visit FAQ” articles. Format video reviews around family experience.
                </div>
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="mb-2.5 text-[12.5px] font-semibold text-[var(--ink-1)]">
            Market trend for this segment
          </div>
          {connected ? (
            <MiEmptyNote />
          ) : (
            <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_24%,transparent)] bg-[var(--gold-tint)] p-4">
              <div className="mb-1.5 font-[var(--font-display)] text-base font-semibold tracking-[-0.02em] text-[var(--gold)]">
                Corporate consolidation
              </div>
              <div className="text-[12.5px] leading-[1.6] text-[var(--ink-2)]">
                DSO chains are aggressively targeting family segments with loss-leader pricing. Autopilot is
                countering with trust-based messaging and review-velocity campaigns.
              </div>
            </div>
          )}
        </div>
      </div>
    </MiCard>
  );
}
